import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export interface Crumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  eyebrow: string;
  title: string;
  description: string;
  crumbs: Crumb[];
  extra?: ReactNode;
}

export default function PageHeader({ eyebrow, title, description, crumbs, extra }: PageHeaderProps) {
  return (
    <section className="w-full bg-primary-900 pt-24 md:pt-32 pb-10 md:pb-14">
      <div className="w-full px-4 md:px-8">
        <div className="w-full max-w-7xl mx-auto">
          <nav className="flex items-center flex-wrap gap-2 text-xs md:text-sm text-background-400">
            {crumbs.map((crumb, idx) => (
              <span key={`${crumb.label}-${idx}`} className="flex items-center gap-2">
                {crumb.to ? (
                  <Link to={crumb.to} className="hover:text-accent-300 transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-background-200">{crumb.label}</span>
                )}
                {idx < crumbs.length - 1 && (
                  <i className="ri-arrow-right-s-line text-background-500"></i>
                )}
              </span>
            ))}
          </nav>

          <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-3xl">
              <span className="text-xs font-semibold tracking-[0.24em] text-accent-300">
                {eyebrow}
              </span>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-bold text-white leading-tight">
                {title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-background-300 leading-8">{description}</p>
            </div>
            {extra && <div className="shrink-0">{extra}</div>}
          </div>
        </div>
      </div>
    </section>
  );
}